




























































































































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import copy from "clipboard-copy";
import { debounce } from "lodash-es";
import uuidv4 from "uuid/v4";
import { Participant, ParticipantStatus } from "@scrile/api-provider/dist/api/ParticipantProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useUserPublicData from "@/hooks/useUserPublicData";
import providers from "@/lib/api";
import { checkEmail } from "@/lib/emailHelpers";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getAbsUrl } from "@/lib/urlHelpers";
import { QuickTip, Tab } from "@/types";
import { EVENTS, ROLES } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseButton from "@/components/BaseButton";
import BaseIconButton from "@/components/BaseIconButton";
import BaseInput from "@/components/BaseInput";
import BaseSegmentedButton from "@/components/BaseSegmentedButton";
import BaseSpinner from "@/components/BaseSpinner";
import ModalView from "@/components/ModalView";
import QuickTipsItem from "@/components/QuickTipsItem";

export default defineComponent({
  name: "ModalParticipantsManage",
  components: {
    QuickTipsItem,
    BaseAvatar,
    BaseButton,
    BaseIconButton,
    BaseInput,
    BaseSegmentedButton,
    BaseSpinner,
    ModalView,
  },
  props: {
    value: { type: Boolean, required: true },
    participants: { type: Array as PropType<Participant[]>, default: () => [] },
    readonly: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { authUser } = useAuthUser();
    const activeTab = ref("username");
    const searchTerm = ref("");
    const emailInput = ref("");
    const workingList = ref<Participant[]>([]);
    const resendingIds = ref<Record<string, boolean>>({});

    const tabs = computed<Tab[]>(() => [
      { text: $t("Username"), value: "username" },
      { text: $t("Email"), value: "email" },
      { text: $t("Link"), value: "link" },
    ]);
    const isUsernameTab = computed(() => activeTab.value === "username");
    const isEmailTab = computed(() => activeTab.value === "email");
    const isLinkTab = computed(() => activeTab.value === "link");
    const isValidEmail = computed(
      () => checkEmail(emailInput.value) && !workingList.value.some(p => p.email === emailInput.value)
    );

    const quickTip = computed<QuickTip>(() => ({
      icon: "msr-info",
      translateKey: isLinkTab.value
        ? $t(
            "Each link works for one person only and only after the application has been submitted. {br} Make sure to generate a new link for every participant and have them sign it after submission in order to receive approval."
          )
        : $t("The invite will be sent only after application submission."),
    }));

    const searchInput = computed(() => ({
      relevantTerm: searchTerm.value || undefined,
      roles: [ROLES.CREATOR, ROLES.FULL_CREATOR],
      disabled: false,
    }));

    const { users: rawUsers, loading, data: searchData, getFirstPage } = useUserPublicData(searchInput);
    const users = computed(() => rawUsers.value.filter(u => u.id !== authUser.value?.id));

    const isInvited = (userId: string) => workingList.value.some(p => p.userId === userId);

    const isEmailParticipant = (participant: Participant) => !!participant.email && !participant.userId;

    const isLinkParticipant = (participant: Participant) =>
      !!participant.urlUuid && !participant.userId && !isEmailParticipant(participant);

    const participantKey = (participant: Participant) =>
      participant.userId || participant.urlUuid || participant.email || participant.id;

    const participantLink = (urlUuid: string) => getAbsUrl(`/participant-consent-link/${encodeURIComponent(urlUuid)}`);

    const participantTitle = (participant: Participant) => {
      if (isEmailParticipant(participant)) return participant.email ?? "";
      if (isLinkParticipant(participant)) return participantLink(participant.urlUuid);
      return participant.user?.screenName ?? "";
    };

    const participantSubtitle = (participant: Participant) => {
      if (isEmailParticipant(participant)) return $t("Invited via email");
      if (isLinkParticipant(participant)) return $t("Invited via link");
      return participant.user ? `@${participant.user.username}` : "";
    };

    const copyParticipantLink = async (urlUuid: string, text = $t("Link copied")) => {
      await copy(participantLink(urlUuid));
      emitter.emit(EVENTS.NOTIFICATION_SHOW, { text });
    };

    const createParticipant = (data: Partial<Participant>): Participant => ({
      id: "",
      postId: "",
      userId: null,
      user: null,
      status: ParticipantStatus.NEW,
      postOwnerId: "",
      shootingLocation: "",
      shootingDate: "",
      fileName: "",
      urlUuid: "",
      email: null,
      ...data,
    });

    const invite = (user: UserPublicData) => {
      if (isInvited(user.id)) return;
      workingList.value.push(
        createParticipant({
          userId: user.id,
          user,
        })
      );
    };

    const generateLink = async () => {
      const urlUuid = uuidv4();
      workingList.value.push(createParticipant({ urlUuid }));
      await copyParticipantLink(urlUuid, $t("Link generated and copied"));
    };

    const inviteByEmail = () => {
      if (!isValidEmail.value) return;
      const urlUuid = uuidv4();
      workingList.value.unshift(createParticipant({ email: emailInput.value, urlUuid }));
      emailInput.value = "";
    };

    const removeFromList = (key: string) => {
      workingList.value = workingList.value.filter(p => participantKey(p) !== key);
    };

    const isRejected = (participant: Participant) => participant.status === ParticipantStatus.REJECTED;

    const canResend = (participant: Participant) => isRejected(participant) && !isLinkParticipant(participant);

    const isResending = (id: string) => resendingIds.value[id];

    const replaceParticipant = (participant: Participant) => {
      workingList.value = workingList.value.map(p => (p.id === participant.id ? participant : p));
    };

    const resend = async (participant: Participant) => {
      if (!participant.id || isResending(participant.id)) return;

      resendingIds.value = { ...resendingIds.value, [participant.id]: true };
      try {
        const updatedParticipant = await providers.ParticipantProvider.resend({ id: participant.id });
        replaceParticipant(updatedParticipant);
        emit("resend", updatedParticipant);
      } finally {
        const nextResendingIds = { ...resendingIds.value };
        delete nextResendingIds[participant.id];
        resendingIds.value = nextResendingIds;
      }
    };

    const onConfirm = () => {
      emit("confirm", [...workingList.value]);
    };

    const onClose = () => {
      emit("input", false);
    };

    const statusLabel = (status: ParticipantStatus) => {
      switch (status) {
        case ParticipantStatus.NEW:
          return $t("Pending");
        case ParticipantStatus.ACCEPTED:
          return $t("Accepted");
        case ParticipantStatus.REJECTED:
          return $t("Rejected");
        default:
          return "";
      }
    };

    const statusClass = (status: ParticipantStatus) => ({
      __pending: status === ParticipantStatus.NEW,
      __accepted: status === ParticipantStatus.ACCEPTED,
      __rejected: status === ParticipantStatus.REJECTED,
    });

    watch(
      searchTerm,
      debounce((term: string) => {
        if (term) {
          getFirstPage();
        } else {
          searchData.value = undefined;
        }
      }, 300)
    );

    watch(
      () => props.value,
      open => {
        if (open) {
          workingList.value = props.participants.map(p => ({ ...p }));
          searchTerm.value = "";
          emailInput.value = "";
          searchData.value = undefined;
        }
      }
    );

    return {
      quickTip,
      activeTab,
      searchTerm,
      emailInput,
      workingList,
      tabs,
      users,
      loading,
      isLinkTab,
      isUsernameTab,
      isEmailTab,
      isValidEmail,
      isInvited,
      isLinkParticipant,
      isEmailParticipant,
      isRejected,
      canResend,
      isResending,
      participantKey,
      participantTitle,
      participantSubtitle,
      copyParticipantLink,
      invite,
      generateLink,
      inviteByEmail,
      removeFromList,
      resend,
      onConfirm,
      onClose,
      statusLabel,
      statusClass,
    };
  },
});
